import { cn } from "../lib/utils";

export interface PaginationProps {
  total: number;
  perPage: number;
  current: number;
  onChange: (page: number) => void;
  maxVisible?: number;
}

export function Pagination({
  total,
  perPage,
  current,
  onChange,
  maxVisible = 5,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;

  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, current - half);
  const end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  const pages: number[] = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="flex gap-1">
      <PageBtn onClick={() => onChange(1)} disabled={current === 1} title="맨 처음">
        «
      </PageBtn>
      <PageBtn onClick={() => onChange(current - 1)} disabled={current === 1}>
        ‹
      </PageBtn>
      {pages.map((p) => (
        <PageBtn key={p} active={p === current} onClick={() => onChange(p)}>
          {p}
        </PageBtn>
      ))}
      <PageBtn
        onClick={() => onChange(current + 1)}
        disabled={current === totalPages}
      >
        ›
      </PageBtn>
      <PageBtn
        onClick={() => onChange(totalPages)}
        disabled={current === totalPages}
        title="맨 끝"
      >
        »
      </PageBtn>
    </div>
  );
}

function PageBtn({
  children,
  onClick,
  active,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "w-7 h-7 rounded-md border text-[11px] flex items-center justify-center transition-colors",
        active
          ? "bg-primary-soft border-primary text-primary font-semibold"
          : "bg-surface border-border-strong text-ink-muted hover:bg-canvas",
        "disabled:opacity-40 disabled:cursor-not-allowed",
      )}
    >
      {children}
    </button>
  );
}
